/******************************************************************************\
 * :$
 *
 * Copyright(c) 2022 SAS Institute Inc., Cary, NC, USA. All Rights Reserved.
 *
 * Name: Layout
 *
 * Purpose: the main layout page for the submission
 *
 * Author: craig (Craig.Simpson@sas.com)
 *
 * Support: SAS(r) Solutions OnDemand
 *
 * Input:
 *
 * Output:
 *
 * Parameters: (if applicable)
 *
 * Dependencies/Assumptions:
 *
 * Usage:
 *
 * History:
 * ddmmmyyyy userid description (Change Code)
 * 04Aug2022 craig File created.
 \******************************************************************************/
import React, {useLayoutEffect, useState} from "react";
import "./Layout.css";
import {Col, Container, Image, Nav, Navbar, Row} from "react-bootstrap";
import 'bootstrap/dist/css/bootstrap.min.css';
import {getGuestCookie, getSessionCookie} from "./context/context";
import {NavLink, Outlet, useLocation} from "react-router-dom";
import {IUser, RoleEnum} from "./models/user/IUser";
import {getDataGet, getDataPost} from "./models/data/Data";
import {ErrorBoundary} from "./views/routes/errors/ErrorBoundary";
import {useIdleTimer} from "react-idle-timer";
import {InfoDialog} from "./components/dialogs/InfoDialog";
import {TimeoutDialog} from "./components/dialogs/TimeoutDialog";
import {IOutcome} from "./models/request/Outcome";

export const Layout: React.FC = (props) => {

    const location = useLocation();
    const [path, setPath] = useState(location.pathname);

    const [sessionUser, setSessionUser] = useState<IUser>(getSessionCookie());
    const [showTimeoutDialog, setShowTimeoutDialog] = useState<boolean>(false);

    /*
       to get the error boundary to reset you need to do the locaiton/path thing
       see: https://stackoverflow.com/questions/71762507/reset-error-boundary-on-back-journey-using-useerrorhandler-hook-from-react-error
     */
    useLayoutEffect(() => setPath(location.pathname), [location.pathname]);

    const GuestNavigation =
        <Navbar>
            <Container>
                <Nav className={"nav-link-sections"}>
                    <NavLink className="nav-link" to="/">WELCOME</NavLink>
                </Nav>
            </Container>
        </Navbar>;

    const UserNavigation = (top:boolean) => {
        return <Navbar>
            <Container>
                <Nav className={"nav-link-sections"}>
                    <NavLink className="nav-link" to="/">WELCOME</NavLink>
                    <NavLink className="nav-link" to="/dashboard">DASHBOARD</NavLink>
                    {/*<NavLink className="nav-link" to="/resources">HISTORY</NavLink>*/}
                    {top &&
                        <NavLink to={"/guide"}><Image className={"timeline-image-nav"} src={"/images/timeline/GS_Guide.png"}/></NavLink>
                    }
                </Nav>
            </Container>
        </Navbar>
    }

    const AdminNavigation = (top:boolean) => {
        return <Navbar>
            <Container>
                <Nav className={"nav-link-sections"}>
                    <NavLink className="nav-link" to="/">WELCOME</NavLink>
                    <NavLink className="nav-link" to="/dashboard">DASHBOARD</NavLink>
                    {/*<NavLink className="nav-link" to="/resources">HISTORY</NavLink>*/}
                    <NavLink className="nav-link" to="/admin">ADMIN</NavLink>
                    {top &&
                        <NavLink to={"/guide"}><Image className={"timeline-image-nav"} src={"/images/timeline/GS_Guide.png"}/></NavLink>
                    }
                </Nav>
            </Container>
        </Navbar>
    }

    const LogoutHandler = (event:React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        const logoutURL = process.env.REACT_APP_API_URL + "/api/form/logoff";
        //remove session on server
        getDataGet(logoutURL).then(response => {
            setSessionUser(getGuestCookie());
            window.location.href = "/";
        })
    }

    const navigation = (top:boolean) => {
        if (sessionUser.RoleID === RoleEnum.Admin || sessionUser.RoleID === RoleEnum.GS_Admin) {
            return AdminNavigation(top);
        } else if (sessionUser.RoleID === RoleEnum.Reviewer) {
            return UserNavigation(top);
        } else {
            return GuestNavigation;
        }
    }

    const loginLink = () => {
        if (sessionUser.UserID > 0) {
            return <NavLink className="user-link" to="/logoff"
                            onClick={LogoutHandler}>{sessionUser.FirstName + " " + sessionUser.LastName} LOG
                OUT</NavLink>
        } else {
            return <NavLink className="user-link" to="/login">LOG IN</NavLink>
        }
    }

    /**
     * onPrompt hook.  Will call 5 min before end of session
     */
    const onPrompt = () => {
        //only show timeout dialog for logged in users (other
        if (sessionUser.UserID > 0) {
            setShowTimeoutDialog(true);
        } else {
            setShowTimeoutDialog(false);
        }
    }

    /**
     * onIdle hook - will call once the session goes idle, for logged in users, log out and redirect to welcome page
     */
    const onIdle = () => {
        setShowTimeoutDialog(false);
        if (sessionUser.UserID > 0) {
            const logoutURL = process.env.REACT_APP_API_URL + "/api/form/logoff";
            //remove session on server
            getDataGet(logoutURL).then(response => {
                setSessionUser(getGuestCookie());
                window.location.href = "/";
            })
        }
        else {
            //don't really care, so just reset the timer
            reset()
        }
    }

    const onActive = (event) => {
        if (sessionUser.UserID > 0) {
            //call keep alive
            handleKeepAlive();
        }
        reset();
    }

    const onAction = (event) => {
        // Do something when a user triggers a watched event
        //console.log("in on action");
    }

    const toggleTimeoutPopup = () => {
        setShowTimeoutDialog(!showTimeoutDialog);
    }

    const handleKeepAlive = () => {
        setShowTimeoutDialog(false);
        const keepaliveURL = process.env.REACT_APP_API_URL + "/api/keepalive";
        getDataPost(keepaliveURL, "").then(response => {
            const outcome: IOutcome = response;
            if (outcome.Success) {
                reset();
            }
        })
    }



    const {
        start,
        reset,
        activate,
        pause,
        resume,
        isIdle,
        isPrompted,
        isLeader,
        getTabId,
        getRemainingTime,
        getElapsedTime,
        getLastIdleTime,
        getLastActiveTime,
        getTotalIdleTime,
        getTotalActiveTime
    } = useIdleTimer({
        onPrompt,
        onIdle,
        onActive,
        onAction,
        timeout: 3300000, // 55 min
        promptTimeout: 300000, // 5 min
        events: [
            'keydown',
            'mousedown'
        ],
        immediateEvents: [],
        debounce: 0,
        throttle: 0,
        eventsThrottle: 200,
        element: document,
        startOnMount: true,
        startManually: false,
        stopOnIdle: false,
        crossTab: false,
        name: 'idle-timer',
        syncTimers: 0,
        leaderElection: false
    })

    return (
        <Container className="App" fluid>
            <TimeoutDialog show={showTimeoutDialog} handleStayLoggedIn={handleKeepAlive}/>
            <Row className="header-container" as="header">
                <Col className="header-nav">
                    <Nav>
                        {loginLink()}
                    </Nav>
                </Col>
            </Row>
            <Row className="sub-header-container">
                <Col className="sub-container">
                    <Row>
                        <Col className="sub-header-nav left">
                            <a href={process.env.REACT_APP_URL}>
                                <img className="gold-standard-logo"
                                     src="/images/ced-cancer-gold-standard-logo.svg"
                                     alt="home"/>
                            </a>
                        </Col>
                        <Col className="sub-header-nav right">
                            {navigation(true)}
                        </Col>
                    </Row>
                </Col>
            </Row>
            <ErrorBoundary key={path}>
                <Row className="main-container">
                    <Col className="sub-container">
                        <Outlet/>
                    </Col>
                </Row>
            </ErrorBoundary>
            <Row className={"footer-container"} as={"footer"}>
                <div className={"footer-body"}>
                    <div className={"footer-main"}>
                        <div className={"footer-top"}>
                            <div className={"footer-nav"}>
                                {navigation(false)}
                            </div>
                            <div className={"footer-social"}>
                                <a href={"https://twitter.com/CEOCancerfight"} rel={"noreferrer"} target={"_blank"}><Image
                                    src={"/images/icons/twitter.png"} height={"25px"}/></a>
                                <a href={"https://www.linkedin.com/company/ceoroundtableoncancer/"} rel={"noreferrer"} target={"_blank"}><Image
                                    src={"/images/icons/linkedin.png"} height={"25px"}/></a>
                            </div>
                        </div>
                        <div className={"footer-info"}>
                            <div className={"ceo-logo"}>
                                <a href={"https://www.ceoroundtableoncancer.org/"} rel={"noreferrer"} target={"_blank"}>
                                    <Image src={"/images/ceort-logo-transp.png"} height={"70px"}/>
                                </a>
                                <div className={"width-smaller"}>
                                    <span>The CEO Cancer Gold Standard&trade; is an initiative of the CEO Roundtable on Cancer.</span>
                                </div>
                            </div>
                            <div className={"copyright-section"}>
                                <span>&copy; {new Date().getFullYear()} CEO Roundtable on Cancer, Inc.</span><span>All rights reserved</span>
                                <div className={"terms-privacy"}>
                                    <a href={"/privacy"} className={"footer-link"}>Privacy Policy</a>
                                    <a href={"/terms"} className={"footer-link"}>Terms of Use</a>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </Row>
        </Container>

    )
}